import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserSessionsQuery } from '../../../../hooks/useDashboardQueries';
import { SessionV2 } from '../../../../types/dashboardV2';
import {
  formatSessionStatus,
  getMobileSessionStatusBadgeClass,
  getMobileSessionStatusDotClass,
} from '../../../../shared/utils/statusUtils';
import { formatDateTime } from '../../../../shared/utils/dateFormat';
import { formatUAHGrouped } from '../../../../shared/utils/currency';
import { HistoryColumn, HistoryPanel } from './HistoryPanel';
import type { PaginationState } from '../../../../hooks/usePaginationState';

const SessionStatusBadge = ({ session }: { session: SessionV2 }) => (
  <span className={`mobile-status-badge ${getMobileSessionStatusBadgeClass(session.chargingStatus)}`}>
    <span className={`mobile-status-dot ${getMobileSessionStatusDotClass(session.chargingStatus)}`} />
    {formatSessionStatus(session.chargingStatus)}
  </span>
);

/** Actual energy charge; a dash while the session is unsettled (never "0"). */
const SettledAmount = ({ session }: { session: SessionV2 }) => {
  const { t } = useTranslation();
  if (session.settledAmount == null) {
    return <span title={t('dashboard.userDetails.settledPending')}>—</span>;
  }
  return <>{formatUAHGrouped(session.settledAmount)}</>;
};

const COLUMNS: HistoryColumn<SessionV2>[] = [
  {
    headerKey: 'dashboard.userDetails.tableSession',
    width: '9%',
    renderCell: (s) => <>#{s.sessionId}</>,
  },
  {
    headerKey: 'dashboard.userDetails.tableStation',
    width: '10%',
    renderCell: (s) => s.idTag,
  },
  {
    headerKey: 'dashboard.userDetails.tableStatus',
    width: '14%',
    renderCell: (s) => <SessionStatusBadge session={s} />,
  },
  {
    headerKey: 'dashboard.userDetails.tableStartedBalance',
    width: '13%',
    numeric: true,
    renderCell: (s) => (s.startedBalance !== undefined ? formatUAHGrouped(s.startedBalance) : '—'),
  },
  {
    headerKey: 'dashboard.userDetails.tableAmount',
    width: '11%',
    numeric: true,
    renderCell: (s) => formatUAHGrouped(s.amount),
  },
  {
    headerKey: 'dashboard.userDetails.tableSettled',
    width: '12%',
    numeric: true,
    renderCell: (s) => <SettledAmount session={s} />,
  },
  {
    headerKey: 'dashboard.userDetails.tableEnergy',
    width: '11%',
    numeric: true,
    renderCell: (s) => `${s.energy.toFixed(2)} kWh`,
  },
  {
    headerKey: 'dashboard.userDetails.tableDate',
    width: '20%',
    numeric: true,
    renderCell: (s) => formatDateTime(s.createdAt),
  },
];

export const SessionsPanel = memo(function SessionsPanel({
  userId,
  pagination,
}: {
  userId: string;
  pagination: PaginationState;
}) {
  const { data, isLoading, error, refetch } = useUserSessionsQuery({
    userId,
    page: pagination.page,
    pageSize: pagination.rowsPerPage,
  });

  return (
    <HistoryPanel<SessionV2>
      items={data?.data ?? []}
      total={data?.total ?? 0}
      isLoading={isLoading}
      error={error}
      onRetry={() => refetch()}
      pagination={pagination}
      errorFallbackKey="dashboard.userDetails.errorSessions"
      loadingKey="dashboard.userDetails.loadingSessions"
      emptyIcon="electric_car"
      emptyTitleKey="dashboard.userDetails.noSessionsYet"
      emptySubtitleKey="dashboard.userDetails.noSessionsHint"
      columns={COLUMNS}
      getRowKey={(s) => s.id}
      renderMobileCard={(s, t) => (
        <div key={s.id} className="user-details-card" role="article">
          <div className="user-details-card-row">
            <div className="user-details-card-title">
              <span className="material-symbols-outlined" aria-hidden="true">electric_car</span>
              {t('dashboard.userDetails.tableSession')} #{s.sessionId}
            </div>
            <SessionStatusBadge session={s} />
          </div>
          <div className="user-details-card-sub">{s.idTag} · {formatDateTime(s.createdAt)}</div>
          <div className="user-details-card-divider" />
          <div className="user-details-card-row">
            <span className="user-details-card-label">{t('dashboard.userDetails.tableAmount')}</span>
            <span className="user-details-card-value">{formatUAHGrouped(s.amount)}</span>
          </div>
          <div className="user-details-card-row">
            <span className="user-details-card-label">{t('dashboard.userDetails.tableSettled')}</span>
            <span className="user-details-card-value"><SettledAmount session={s} /></span>
          </div>
          <div className="user-details-card-row">
            <span className="user-details-card-label">{t('dashboard.userDetails.tableEnergy')}</span>
            <span className="user-details-card-value">{s.energy.toFixed(2)} kWh</span>
          </div>
          <div className="user-details-card-row">
            <span className="user-details-card-label">{t('dashboard.userDetails.tableStartedBalance')}</span>
            <span className="started-balance-chip">
              <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }} aria-hidden="true">account_balance_wallet</span>
              {s.startedBalance !== undefined ? formatUAHGrouped(s.startedBalance) : '—'}
            </span>
          </div>
        </div>
      )}
    />
  );
});
